import React, { useEffect, useState } from 'react'
import { LoadingOverlay, SimpleGrid, Stack } from '@mantine/core'
import { useSearchParams } from 'react-router-dom'
import { getPromotionReportsByPhone } from '../api/reportService'
import { IsaacTemplateMerchantResVO } from '../typings/promotionTypes'
import { showNotification } from '../utils/notificationUtil'
import { ThemeProvider } from '../components/ThemeProvider'
import { getThemeConfig } from '../utils/themeConfig'
import { isPC } from '../utils/deviceUtil'
import SearchSection from './components/SearchSection'
import ReportCard from './components/ReportCard'
import EmptyState from './components/EmptyState'
import { clearSearchHistory, getSearchHistory, saveSearchHistory, type SearchHistory } from './utils/searchHistory'

function PromotionReportsContent() {
  const [phone, setPhone] = useState('')
  const [reports, setReports] = useState<IsaacTemplateMerchantResVO[]>()
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([])

  const isPCDevice = isPC()

  useEffect(() => {
    setSearchHistory(getSearchHistory())
  }, [])

  const handleSearch = async (phoneNumber?: string) => {
    const targetPhone = phoneNumber || phone

    if (!targetPhone) {
      showNotification.info('请输入手机号')
      return
    }

    if (!/^1[3-9]\d{9}$/.test(targetPhone)) {
      showNotification.error('请输入正确的手机号')
      return
    }

    if (phoneNumber && phoneNumber !== phone) {
      setPhone(phoneNumber)
    }

    try {
      setLoading(true)
      setSearched(true)
      const data = await getPromotionReportsByPhone(targetPhone)
      setReports(data)
      saveSearchHistory(targetPhone)
      setSearchHistory(getSearchHistory())
    } catch (error) {
      showNotification.error('查询失败')
      console.error('Failed to fetch promotion reports:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setPhone('')
    setReports(undefined)
    setSearched(false)
  }

  const handleClearHistory = () => {
    clearSearchHistory()
    setSearchHistory([])
    showNotification.success('历史记录已清除')
  }

  const containerClass = isPCDevice
    ? 'w-full max-w-container-md 2xl:max-w-container-lg mx-auto'
    : 'w-full max-w-xl mx-auto'

  return (
    <div className={`min-h-screen ${containerClass}`} style={{ backgroundColor: '#f8f9fa' }}>
      <LoadingOverlay zIndex={1000} visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />

      <Stack p={isPCDevice ? 'xl' : 'md'} gap={isPCDevice ? 'xl' : 'lg'}>
        <SearchSection
          phone={phone}
          setPhone={setPhone}
          onSearch={handleSearch}
          onReset={handleReset}
          onClearHistory={handleClearHistory}
          searchHistory={searchHistory}
          loading={loading}
        />

        {!loading && searched && reports && reports.length > 0 && (
          <SimpleGrid cols={{ base: 1, sm: 1, md: 2, lg: 2, xl: 3 }} spacing={{ base: 'md', lg: 'lg' }}>
            {reports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </SimpleGrid>
        )}

        {!loading && searched && reports?.length === 0 && <EmptyState type="no-results" />}

        {!searched && !loading && <EmptyState type="initial" />}
      </Stack>
    </div>
  )
}

export default function PromotionReports() {
  const [searchParams] = useSearchParams()
  const themeType = Number(searchParams.get('theme')) || 1
  const themeConfig = getThemeConfig(themeType)

  return (
    <ThemeProvider theme={themeConfig}>
      <PromotionReportsContent />
    </ThemeProvider>
  )
}
